import { SideStatus } from '../../domain/Side';
import { ReanimationReason } from '../reanimation/ReanimationManager';
/**
 * Player choice types for UI interaction
 */
export var PlayerChoiceType;
(function (PlayerChoiceType) {
    PlayerChoiceType["TRAP_ESCAPE"] = "trap_escape";
    PlayerChoiceType["REANIMATION"] = "reanimation";
    PlayerChoiceType["ALLIANCE"] = "alliance";
    PlayerChoiceType["SURRENDER"] = "surrender";
})(PlayerChoiceType || (PlayerChoiceType = {}));
/**
 * PlayerChoiceHandler - Manages player choices for game mechanics
 *
 * Bridges game mechanics with UI for player decisions:
 * - Trap escape choices
 * - Territory reanimation
 * - Alliance decisions
 */
export class PlayerChoiceHandler {
    board;
    trapResolver;
    reanimationManager;
    eventBus;
    pendingChoices = new Map();
    defaultTimeLimit = 30; // seconds
    constructor(board, trapResolver, reanimationManager, eventBus) {
        this.board = board;
        this.trapResolver = trapResolver;
        this.reanimationManager = reanimationManager;
        this.eventBus = eventBus;
        this.setupEventListeners();
    }
    /**
     * Creates trap escape choice request
     */
    createTrapEscapeChoice(situation, playerId) {
        const requestId = `trap-${Date.now()}-${playerId}`;
        const options = [];
        // Get potential recipients
        const recipients = this.getPotentialRecipients(situation);
        for (const recipientId of recipients) {
            const recipientSide = this.board.getSide(recipientId);
            if (!recipientSide)
                continue;
            const isAlly = this.checkAlliance(situation.trappedSide, recipientId);
            const isEliminated = recipientSide.isEliminated;
            options.push({
                id: `give-to-${recipientId}`,
                label: `Give to Side ${recipientId}`,
                description: this.getRecipientDescription(recipientId, isAlly, isEliminated),
                icon: isAlly ? 'handshake' : isEliminated ? 'life-ring' : 'gift',
                recommended: isAlly,
                consequences: this.predictConsequences(situation.trappedPosition, recipientId),
            });
        }
        // Add strategic options
        options.push({
            id: 'give-to-weakest',
            label: 'Give to Weakest Enemy',
            description: 'Strategic choice to keep balance',
            icon: 'balance-scale',
            consequences: ['Maintains game balance', 'Prevents runaway leader'],
        });
        const request = {
            id: requestId,
            type: PlayerChoiceType.TRAP_ESCAPE,
            playerId,
            title: 'Choose Territory Recipient',
            description: `You are trapped at ${situation.trappedPosition} but can escape. Choose who receives this territory.`,
            options,
            timeLimit: this.defaultTimeLimit,
            context: {
                situation,
                position: situation.trappedPosition.toString(),
                surroundingSides: situation.surroundingSides,
            },
        };
        this.pendingChoices.set(requestId, request);
        return request;
    }
    /**
     * Creates reanimation choice request
     */
    createReanimationChoice(givingSide, availableTerritories) {
        const requestId = `reanimate-${Date.now()}-${givingSide}`;
        const eliminatedSides = this.reanimationManager.getEliminatedSides();
        const options = [];
        for (const eliminatedId of eliminatedSides) {
            const eliminatedSide = this.board.getSide(eliminatedId);
            if (!eliminatedSide)
                continue;
            options.push({
                id: `reanimate-${eliminatedId}`,
                label: `Reanimate Side ${eliminatedId}`,
                description: `Bring Side ${eliminatedId} back into the game`,
                icon: 'resurrection',
                consequences: [
                    'Side returns with one territory',
                    'Creates new strategic dynamics',
                    'May form alliance',
                ],
            });
        }
        // Add option to not reanimate
        options.push({
            id: 'no-reanimation',
            label: 'Keep Territory',
            description: 'Do not reanimate anyone',
            icon: 'times',
            consequences: ['Maintain current position'],
        });
        const request = {
            id: requestId,
            type: PlayerChoiceType.REANIMATION,
            playerId: givingSide,
            title: 'Reanimate Eliminated Player?',
            description: 'You can give a territory to bring an eliminated player back',
            options,
            timeLimit: this.defaultTimeLimit * 2, // More time for strategic decision
            context: {
                availableTerritories,
                eliminatedSides,
            },
        };
        this.pendingChoices.set(requestId, request);
        return request;
    }
    /**
     * Processes player's choice response
     */
    async processChoice(response) {
        const request = this.pendingChoices.get(response.requestId);
        if (!request) {
            return {
                success: false,
                message: 'Choice request not found or expired',
                effects: [],
            };
        }
        this.pendingChoices.delete(response.requestId);
        switch (request.type) {
            case PlayerChoiceType.TRAP_ESCAPE:
                return this.processTrapChoice(request, response);
            case PlayerChoiceType.REANIMATION:
                return this.processReanimationChoice(request, response);
            default:
                return {
                    success: false,
                    message: 'Unknown choice type',
                    effects: [],
                };
        }
    }
    /**
     * Processes trap escape choice
     */
    processTrapChoice(request, response) {
        const situation = request.context.situation;
        const effects = [];
        let recipientId;
        if (response.chosenOptionId === 'give-to-weakest') {
            // ВАЖНО: Выбираем слабейшего ТОЛЬКО из окружающих сторон
            const validSurroundingSides = situation.surroundingSides.filter((id) => id !== situation.trappedSide);
            recipientId = this.findWeakestEnemyFromList(situation.trappedSide, validSurroundingSides);
            // Если не нашли слабейшего, берём первого из окружающих
            if (recipientId === -1 && validSurroundingSides.length > 0) {
                recipientId = validSurroundingSides[0];
            }
        }
        else {
            recipientId = parseInt(response.chosenOptionId.replace('give-to-', ''));
        }
        const trapChoice = {
            giveTerritoryTo: recipientId,
            reason: this.determineChoiceReason(situation.trappedSide, recipientId),
        };
        const resolution = this.trapResolver.resolveByChoice(situation, trapChoice);
        effects.push(`Territory given to Side ${recipientId}`);
        if (resolution.sideEliminated) {
            effects.push(`Side ${situation.trappedSide} eliminated`);
        }
        // Check if recipient was reanimated
        const recipientSide = this.board.getSide(recipientId);
        if (recipientSide?.status === SideStatus.REANIMATED) {
            effects.push(`Side ${recipientId} has been reanimated!`);
        }
        return {
            success: true,
            message: `Territory transferred to Side ${recipientId}`,
            effects,
        };
    }
    /**
     * Processes reanimation choice
     */
    processReanimationChoice(request, response) {
        const effects = [];
        if (response.chosenOptionId === 'no-reanimation') {
            return {
                success: true,
                message: 'No reanimation performed',
                effects: ['Territory kept'],
            };
        }
        const toReanimate = parseInt(response.chosenOptionId.replace('reanimate-', ''));
        const territories = request.context.availableTerritories;
        // Use first available territory (UI can be enhanced to let player choose)
        const territory = territories[0];
        const reanimationRequest = {
            fromSide: request.playerId,
            toSide: toReanimate,
            territory: territory,
            reason: ReanimationReason.PLAYER_GIFT,
        };
        const result = this.reanimationManager.processReanimation(reanimationRequest);
        if (result.success) {
            effects.push(`Side ${toReanimate} reanimated at ${territory}`);
            effects.push('New alliance potential created');
        }
        return {
            success: result.success,
            message: result.message || 'Reanimation processed',
            effects,
        };
    }
    /**
     * Gets potential recipients for territory
     */
    getPotentialRecipients(situation) {
        const recipients = [];
        for (const sideId of situation.surroundingSides) {
            if (sideId !== situation.trappedSide) {
                recipients.push(sideId);
            }
        }
        // Add eliminated sides as potential recipients (for reanimation)
        const eliminatedSides = this.reanimationManager.getEliminatedSides();
        recipients.push(...eliminatedSides);
        return [...new Set(recipients)]; // Remove duplicates
    }
    /**
     * Gets description for recipient
     */
    getRecipientDescription(recipientId, isAlly, isEliminated) {
        if (isEliminated) {
            return `Reanimate eliminated Side ${recipientId} back into the game`;
        }
        if (isAlly) {
            return `Support your ally Side ${recipientId}`;
        }
        return `Give territory to Side ${recipientId}`;
    }
    /**
     * Predicts consequences of giving territory
     */
    predictConsequences(position, recipientId) {
        const consequences = [];
        const side = this.board.getSide(recipientId);
        if (!side)
            return consequences;
        if (side.isEliminated) {
            consequences.push('Brings eliminated player back');
            consequences.push('Creates potential ally');
        }
        // Check if this would trigger chain captures
        const neighbors = position.getOrthogonalNeighbors();
        let potentialCaptures = 0;
        for (const neighbor of neighbors) {
            const territory = this.board.getTerritoryAt(neighbor);
            if (territory && territory.owner !== recipientId) {
                // Check if would be surrounded
                potentialCaptures++;
            }
        }
        if (potentialCaptures > 0) {
            consequences.push(`May trigger ${potentialCaptures} capture(s)`);
        }
        return consequences;
    }
    /**
     * Checks if two sides are allied
     */
    checkAlliance(side1, side2) {
        // Player 1 owns odd sides (1,3,5,7,9)
        // Player 2 owns even sides (0,2,4,6,8)
        // Sides are allied if they belong to the same player
        const player1 = side1 % 2 === 1 ? 1 : 2;
        const player2 = side2 % 2 === 1 ? 1 : 2;
        return player1 === player2;
    }
    /**
     * Finds weakest enemy from a specific list of sides
     */
    findWeakestEnemyFromList(excludeSide, candidateSides) {
        let weakestSide = -1;
        let minTerritories = Infinity;
        for (const sideId of candidateSides) {
            if (sideId === excludeSide)
                continue;
            const side = this.board.getSide(sideId);
            if (!side || !side.isActive)
                continue;
            if (side.territories.size < minTerritories) {
                minTerritories = side.territories.size;
                weakestSide = sideId;
            }
        }
        return weakestSide;
    }
    /**
     * Finds weakest enemy side
     */
    findWeakestEnemy(excludeSide) {
        let weakestSide = -1; // Fixed: Initialize with -1 instead of 0 to avoid defaulting to side 0
        let minTerritories = Infinity;
        for (const [sideId, side] of this.board.sides) {
            if (sideId === excludeSide || !side.isActive)
                continue;
            if (side.territories.size < minTerritories) {
                minTerritories = side.territories.size;
                weakestSide = sideId;
            }
        }
        // If no valid enemy found, return first active side that's not excluded
        if (weakestSide === -1) {
            for (const [sideId, side] of this.board.sides) {
                if (sideId !== excludeSide && side.isActive) {
                    return sideId;
                }
            }
        }
        return weakestSide;
    }
    /**
     * Determines choice reason for logging
     */
    determineChoiceReason(from, to) {
        if (this.checkAlliance(from, to))
            return 'ally';
        const weakest = this.findWeakestEnemy(from);
        if (weakest === to)
            return 'weakest_enemy';
        return 'strategic';
    }
    /**
     * Sets up event listeners
     */
    setupEventListeners() {
        // Listen for trap situations - no need, handled by IntegratedGameEngine
        // Listen for player choices from UI
        this.eventBus.subscribe('CHOICE_RESPONSE', async (response) => {
            console.log('[PlayerChoiceHandler] Received CHOICE_RESPONSE:', response);
            const result = await this.processChoice(response);
            if (result.success) {
                console.log('[PlayerChoiceHandler] Choice processed successfully:', result.message);
                // Emit success event for UI feedback
                this.eventBus.emit('CHOICE_RESOLVED', {
                    requestId: response.requestId,
                    result
                });
            }
            else {
                console.error('[PlayerChoiceHandler] Choice processing failed:', result.message);
            }
        });
        // Listen for choice timeout
        this.eventBus.subscribe('CHOICE_TIMEOUT', (event) => {
            this.handleTimeout(event.requestId);
        });
    }
    /**
     * Handles choice timeout (auto-select)
     */
    handleTimeout(requestId) {
        const request = this.pendingChoices.get(requestId);
        if (!request)
            return;
        // Auto-select first option
        const autoResponse = {
            requestId,
            chosenOptionId: request.options[0]?.id || 'default',
            timestamp: new Date(),
        };
        this.processChoice(autoResponse);
    }
    /**
     * Gets all pending choices for a player
     */
    getPendingChoices(playerId) {
        const choices = [];
        for (const request of this.pendingChoices.values()) {
            if (request.playerId === playerId) {
                choices.push(request);
            }
        }
        return choices;
    }
    /**
     * Cancels a pending choice
     */
    cancelChoice(requestId) {
        return this.pendingChoices.delete(requestId);
    }
}
//# sourceMappingURL=PlayerChoiceHandler.js.map